import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import NavBar from '../shared/NavBar';
import { SuperDuperProvider, useSuperDuper } from './context/SuperDuperContext';
import { useAdminAuthGuard } from './hooks/useAdminAuthGuard';
import DeleteQuizDialog from './components/DeleteQuizDialog';
import AppSnackbar from './components/AppSnackbar';
import BaseCampUserAgreementGate from './components/BaseCampUserAgreementGate';
import { TABS } from './tabs';
import { useAuth } from '../../context/AuthContext';

const SuperDuperDashboard = () => {
  const { handleLogout } = useAdminAuthGuard();
  const { user } = useAuth();
  const context = useSuperDuper();
  const { analyticsDays } = context;
  const [tabValue, setTabValue] = useState(0);
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const userMenuOpen = Boolean(userMenuAnchor);

  const sessionLabel = user?.displayName || user?.username || 'User';

  const allowedTabs = Array.isArray(user?.tabs) ? user.tabs : [];
  const visibleTabs = TABS.filter((tab) => allowedTabs.includes(tab.id));

  useEffect(() => {
    setTabValue(0);
  }, [user?.username, user?.role]);

  useEffect(() => {
    if (tabValue >= visibleTabs.length) {
      setTabValue(0);
    }
  }, [tabValue, visibleTabs.length]);

  useEffect(() => {
    const fetcher = visibleTabs[tabValue]?.onActivate;
    if (!fetcher) return;
    if (fetcher === 'fetchAnalytics') {
      context.fetchAnalytics(analyticsDays);
    } else {
      context[fetcher]();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabValue, visibleTabs.length]);

  const ActiveTab = visibleTabs[tabValue]?.Component;

  return (
    <Box sx={{ bgcolor: '#FAFAFA', minHeight: '100vh', overflowX: 'hidden' }}>
      <NavBar />
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 }, px: { xs: 1.5, sm: 2, md: 3 } }}>
        {/* Header Row - Title and Logout Button */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: { xs: 'flex-start', sm: 'center' },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 2 }
          }}
        >
          <Box sx={{ display: 'inline-flex', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography
              component="span"
              sx={{
                color: '#FF6B35',
                fontWeight: 600,
                fontSize: { xs: '0.65rem', sm: '0.75rem' },
                lineHeight: 1,
                letterSpacing: '0.04em',
                mb: { xs: 0.35, sm: 0.5 },
                opacity: 0.95,
              }}
            >
              YoungPresso
            </Typography>
            <Typography
              variant="h4"
              component="span"
              sx={{
                color: '#FF6B35',
                fontWeight: 700,
                fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' },
                lineHeight: 1.1,
              }}
            >
              BaseCamp
              <Typography
                component="sup"
                sx={{
                  fontSize: '0.38em',
                  fontWeight: 600,
                  lineHeight: 0,
                  verticalAlign: 'super',
                  ml: 0.2,
                }}
              >
                TM
              </Typography>
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              alignSelf: { xs: 'stretch', sm: 'auto' },
              flexWrap: 'wrap',
              justifyContent: { xs: 'stretch', sm: 'flex-end' },
            }}
          >
            <Button
              variant="outlined"
              color="inherit"
              onClick={(event) => setUserMenuAnchor(event.currentTarget)}
              endIcon={<KeyboardArrowDownIcon />}
              startIcon={<AccountCircleIcon />}
              size="small"
              aria-controls={userMenuOpen ? 'basecamp-user-menu' : undefined}
              aria-haspopup="true"
              aria-expanded={userMenuOpen ? 'true' : undefined}
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                px: { xs: 2, sm: 2.5 },
                py: { xs: 0.75, sm: 1 },
                textTransform: 'none',
                borderColor: 'divider',
                flex: { xs: 1, sm: '0 1 auto' },
                maxWidth: { xs: '100%', sm: 280 },
                '& .MuiButton-endIcon': { ml: 0.25 },
              }}
            >
              <Box
                component="span"
                sx={{
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {sessionLabel}
              </Box>
            </Button>
            <Menu
              id="basecamp-user-menu"
              anchorEl={userMenuAnchor}
              open={userMenuOpen}
              onClose={() => setUserMenuAnchor(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              PaperProps={{
                sx: { mt: 0.75, minWidth: 220, borderRadius: 2 },
              }}
            >
              <Box sx={{ px: 2, py: 1.25 }}>
                <Typography variant="subtitle2" color="text.secondary">
                  User
                </Typography>
                {user?.displayName && (
                  <Typography variant="body2" sx={{ fontWeight: 600, mt: 0.25 }}>
                    {user.displayName}
                  </Typography>
                )}
                <Typography
                  variant="body2"
                  color={user?.displayName ? 'text.secondary' : 'text.primary'}
                  sx={{ fontWeight: user?.displayName ? 400 : 600, mt: user?.displayName ? 0.25 : 0.5 }}
                >
                  {user?.username || '—'}
                </Typography>
              </Box>
              <Divider />
              <MenuItem
                onClick={() => {
                  setUserMenuAnchor(null);
                  handleLogout();
                }}
                sx={{ color: 'error.main', py: 1.25 }}
              >
                <ListItemIcon>
                  <LogoutIcon fontSize="small" color="error" />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
            <Button
              variant="outlined"
              color="error"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              size="small"
              sx={{
                fontWeight: 600,
                borderRadius: 2,
                px: { xs: 2, sm: 3 },
                py: { xs: 0.75, sm: 1 },
                flex: { xs: 1, sm: '0 0 auto' },
                '&:hover': {
                  backgroundColor: 'rgba(211, 47, 47, 0.08)',
                  borderColor: '#d32f2f',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 8px rgba(211, 47, 47, 0.2)',
                }
              }}
            >
              Logout
            </Button>
          </Box>
        </Box>
        <Box sx={{ mb: { xs: 2, sm: 4 }, borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={visibleTabs.length ? tabValue : false}
            onChange={(event, newValue) => setTabValue(newValue)}
            aria-label="admin dashboard tabs"
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: { xs: 44, sm: 48 },
              '& .MuiTab-root': {
                minHeight: { xs: 44, sm: 48 },
                minWidth: { xs: 'auto', sm: 90 },
                px: { xs: 1.25, sm: 2 },
                fontSize: { xs: '0.8rem', sm: '0.875rem' },
                textTransform: 'none',
              },
              '& .MuiTab-iconWrapper': {
                marginBottom: '0 !important',
                marginRight: { xs: 0.5, sm: 1 },
              },
            }}
          >
            {visibleTabs.map((tab, index) => (
              <Tab
                key={tab.id}
                label={tab.label}
                id={`tab-${index}`}
                icon={tab.icon}
                iconPosition="start"
              />
            ))}
          </Tabs>
        </Box>

        {ActiveTab ? (
          <ActiveTab />
        ) : (
          <Typography sx={{ mt: 4 }} color="text.secondary">
            No tabs are assigned to this account. Ask a super-admin to grant access.
          </Typography>
        )}
      </Container>

      {/* Delete Quiz Confirmation Dialog */}
      <DeleteQuizDialog />

      {/* Snackbar for notifications */}
      <AppSnackbar />
    </Box>
  );
};

const SuperDuper = () => (
  <BaseCampUserAgreementGate>
    <SuperDuperProvider>
      <SuperDuperDashboard />
    </SuperDuperProvider>
  </BaseCampUserAgreementGate>
);

export default SuperDuper;
